export function DecisionMakingFramework() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            How to Make the Right Decision for Your Situation
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Use this framework to objectively evaluate whether selling AS IS or remodeling makes more sense for your specific apartment building and circumstances.
          </p>
        </div>

        {/* Decision Framework */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 mb-16 border-2 border-gray-200">
          <h3 className="text-3xl font-bold mb-8 text-center">The 5-Question Decision Framework</h3>
          
          <div className="space-y-8">
            {/* Question 1 */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-4">Can you afford to wait 12-18 months for your money?</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <div className="font-bold text-red-700 mb-2">If NO → Sell AS IS</div>
                      <p className="text-gray-700 text-sm">You need liquidity now, not in 1-2 years. Renovations tie up your capital and delay your sale significantly.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="font-bold text-gray-700 mb-2">If YES → Consider Remodel</div>
                      <p className="text-gray-700 text-sm">You have the financial cushion to wait, but still need to evaluate the other factors below.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 2 */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-4">Do you have $500K-$2M+ in liquid capital for renovations?</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <div className="font-bold text-red-700 mb-2">If NO → Sell AS IS</div>
                      <p className="text-gray-700 text-sm">Taking on debt for renovations adds risk and interest costs. Don't leverage yourself into a dangerous position.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="font-bold text-gray-700 mb-2">If YES → Consider Remodel</div>
                      <p className="text-gray-700 text-sm">You have the capital, but is this the best use of it? Continue evaluating.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 3 */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-4">Can your market support 20%+ rent increases after renovation?</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <div className="font-bold text-red-700 mb-2">If NO → Sell AS IS</div>
                      <p className="text-gray-700 text-sm">If market rents won't support significant increases, renovations won't pay off. You'll lose money on the investment.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="font-bold text-gray-700 mb-2">If YES → Consider Remodel</div>
                      <p className="text-gray-700 text-sm">Strong rent growth potential is essential, but you still need to verify the math works.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 4 */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold">
                  4
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-4">Do you have experience managing major construction projects?</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <div className="font-bold text-red-700 mb-2">If NO → Sell AS IS</div>
                      <p className="text-gray-700 text-sm">First-time renovators almost always face cost overruns, delays, and stress. Don't learn expensive lessons on your own property.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="font-bold text-gray-700 mb-2">If YES → Consider Remodel</div>
                      <p className="text-gray-700 text-sm">Experience helps, but even pros face challenges. Make sure the numbers still work.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 5 */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold">
                  5
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-4">Will the increased property value exceed total costs by 50%+?</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <div className="font-bold text-red-700 mb-2">If NO → Sell AS IS</div>
                      <p className="text-gray-700 text-sm">If you can't achieve at least 50% return on your renovation investment, it's not worth the risk and effort.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="font-bold text-gray-700 mb-2">If YES → Maybe Remodel</div>
                      <p className="text-gray-700 text-sm">If you answered YES to all 5 questions, renovation might make sense. But most owners can't honestly say yes to all five.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* The Reality */}
        <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-10 mb-12 border-2 border-teal-200">
          <h3 className="text-3xl font-bold mb-6 text-center">The Honest Truth</h3>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-800 mb-6 leading-relaxed">
              If you answered <strong>"NO"</strong> to even ONE of these questions, selling AS IS is almost certainly your best option.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Most apartment building owners we work with answer "NO" to at least 3-4 of these questions. That's why selling AS IS to professional investors makes so much sense—you avoid all the risk, cost, and stress while still getting a fair price for your property.
            </p>
            <div className="bg-white rounded-xl p-8 shadow-lg inline-block">
              <p className="text-2xl font-bold text-teal-600 mb-2">Ready to sell AS IS?</p>
              <p className="text-gray-700">Get your fair cash offer in 24-48 hours</p>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/fc1c41b8ee8740cc5c6b0b621567777d.jpg"
            alt="Decision making framework for selling apartment building"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}